import React, { ChangeEvent, useState } from "react";
import { Box, Button, FormHelperText, TextField } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { ServiceReportFile, ServiceReportFileTypeCode } from "../models/ServiceReportsModel";
import ReportFile from "./ReportFile";
import { MAX_FILE_SIZE_MB } from "../../../shared/models/models";
import { dt } from "../utils/dt";
import useTranslation from "../../../shared/hooks/useTranslation";

interface ServiceReportUploadFiles {
  files: ServiceReportFile[] | null;
  onChange?: (files: ServiceReportFile[] | null) => void;
  typeCode?: ServiceReportFileTypeCode;
  isCreate: boolean;
  maxFileSizeMb?: number;
  marginTop?: number;
}

const ServiceReportUploadFiles = ({
  onChange,
  typeCode,
  files,
  isCreate = false,
  maxFileSizeMb = MAX_FILE_SIZE_MB,
  marginTop = 0
}: ServiceReportUploadFiles) => {
  const [newFiles, setFiles] = useState<ServiceReportFile[]>(files || []);
  const [error, setError] = useState<string>();
  const [canDrop, setCanDrop] = useState(false);
  const { translate } = useTranslation("common");
  const { translate: tMachinery } = useTranslation("myMachinery");

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    handleFileChange(event.dataTransfer.files);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files ? Array.from(event.target.files) : [];

    selectedFiles.forEach((file, i) => {
      processFile(file);
    });

    event.target.value = "";
  };

  const processFile = async (file: File) => {
    setError(undefined);

    if (file.size > maxFileSizeMb * 1024 * 1024) {
      onChange?.(null);
      setError(translate("uploadFile.fileIsTooBig"));
      return;
    }

    const url = await detectUrl(file);

    if (url) {
      const newFile: ServiceReportFile = {
        name: file.name,
        content: url,
        typeCode: typeCode,
        size: file.size
      };

      setFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, newFile];
        onChange?.(updatedFiles);
        return updatedFiles;
      });
    }
  };

  const detectUrl = (file: Blob | null) => {
    return new Promise<string | null>((resolve, reject) => {
      if (!file) {
        resolve(null);
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleDelete = (fileIndex: number) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, i) => i !== fileIndex);
      onChange?.(updatedFiles);
      return updatedFiles;
    });
  };

  return (
    <>
      <label htmlFor="file-input" style={{ cursor: "pointer", display: "block" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            border: `2px dashed ${canDrop ? dt.blue : error ? "#E74C3C" : dt.cardBorder}`,
            borderRadius: dt.radiusSm,
            padding: "24px 20px",
            cursor: "pointer",
            transition: "all 0.2s ease",
            backgroundColor: canDrop ? "rgba(52,152,219,0.04)" : "#FAFBFC",
            "&:hover": {
              borderColor: dt.blue,
              backgroundColor: "rgba(52,152,219,0.04)",
            },
          }}
          onDrop={(event) => {
            event.preventDefault();
            handleFileDrop(event);
            setCanDrop(false);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setCanDrop(true);
          }}
          onDragLeave={() => setCanDrop(false)}
        >
          <CloudUploadOutlinedIcon
            sx={{
              fontSize: 32,
              color: canDrop ? dt.blue : dt.textMuted,
              transition: "color 0.2s ease",
            }}
          />
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 600,
              color: dt.textSecondary,
              textAlign: "center",
            }}
          >
            {tMachinery("dropFilesHere")}
          </Box>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "11px",
              color: dt.textMuted,
              textAlign: "center",
            }}
          >
            {tMachinery("supportedFormatsHint")}
          </Box>
        </Box>
      </label>

      {!!error && (
        <FormHelperText
          error
          sx={{
            padding: "6px 8px",
            margin: 0,
            textAlign: "center",
            fontFamily: dt.font,
          }}
        >
          {error}
        </FormHelperText>
      )}

      <Box mt={marginTop}>
        <TextField
          type="file"
          id="file-input"
          sx={{ display: "none" }}
          inputProps={{ multiple: true }}
          onChange={handleFileChange}
        />

        {newFiles.length > 0 && (
          <Box className="flex flex-col gap-2" sx={{ mt: 1 }}>
            {newFiles.map((file, i) => (
              <ReportFile key={i} file={file} onDelete={() => handleDelete(i)} canDownload={!isCreate} fileIndex={i} />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default ServiceReportUploadFiles;
